import { defineStore } from 'pinia'
import CompanyDashboardResource from '@/api/resources/companyDashboard'
import type { ICompanyDashboard } from '@/interfaces/entities/companyDashboard'
import type { IWidget, WidgetType } from '@/interfaces/components/dashboard/widget'
import type { IIndicatorSourceData, IBarChartSourceData, ITotalizerSourceData } from '@/interfaces/api/widget'
import { resolveWidgetDataFields, resolveWidgetUnit } from '@/utils/sensor-schema-fields'
import {
  getWidgetGroupId,
  getWidgetSensorId,
  indicatorSourceKeyFromResult,
  indicatorSourceKeyFromWidget,
  isGroupWidget,
} from '@/utils/dashboard/widget-source'

interface IWidgetDataState {
  value: number | null
  unit: string
  loading: boolean
  error: boolean
}

interface IBarChartWidgetState {
  loading: boolean
  error: boolean
}

export interface ITotalizerWidgetState {
  demand: ITotalizerSourceData['demand'] | null
  consumption: ITotalizerSourceData['consumption'] | null
  utilization: ITotalizerSourceData['utilization'] | null
  mixing_cycle: ITotalizerSourceData['mixing_cycle'] | null
  loading: boolean
  error: boolean
}

interface ICompanyDashboardStore {
  activeDashboard: ICompanyDashboard | null
  period: [Date, Date] | null
  widgetData: Record<string, IWidgetDataState>
  indicatorSources: Record<string, IIndicatorSourceData>
  barChartSources: Record<string, IBarChartSourceData>
  barChartWidgetStates: Record<string, IBarChartWidgetState>
  totalizerSources: Record<string, ITotalizerSourceData>
  totalizerWidgetStates: Record<string, ITotalizerWidgetState>
  dashboards: ICompanyDashboard[]
}

const AGGREGATION_WIDGET_TYPES: WidgetType[] = [
  'indicator',
  'gauge',
  'status-panel',
  'pie-chart',
]

const INDICATOR_VALUE_WIDGET_TYPES: WidgetType[] = [
  'indicator',
  'gauge',
  'status-panel',
]

function barChartSourceKey(windowPeriod: string, sensorId: string, dataField: string): string {
  return `${windowPeriod}:${sensorId}:${dataField}`
}

function isAggregationWidget(widgetType: WidgetType): boolean {
  return INDICATOR_VALUE_WIDGET_TYPES.includes(widgetType)
}

function usesWidgetDataFetch(widget: IWidget): boolean {
  if (isAggregationWidget(widget.widget_type) || widget.widget_type === 'pie-chart') return true
  return widget.widget_type === 'bar-chart' && isGroupWidget(widget)
}

function usesIndicatorSources(widget: IWidget): boolean {
  if (AGGREGATION_WIDGET_TYPES.includes(widget.widget_type)) return true
  return widget.widget_type === 'bar-chart' && isGroupWidget(widget)
}

export const useCompanyDashboardStore = defineStore('company-dashboard', {
  state: (): ICompanyDashboardStore => ({
    activeDashboard: null,
    period: null,
    dashboards: [] as ICompanyDashboard[],
    widgetData: {},
    indicatorSources: {},
    barChartSources: {},
    barChartWidgetStates: {},
    totalizerSources: {},
    totalizerWidgetStates: {},
  }),

  getters: {
    getDashboards: (state): ICompanyDashboard[] => {
      return state.dashboards
    },

    widgets: (state): IWidget[] => {
      return state.activeDashboard?.dashboard_widgets || []
    },

    aggregationWidgets: (state): IWidget[] => {
      return (
        state.activeDashboard?.dashboard_widgets.filter((w) => usesIndicatorSources(w)) || []
      )
    },
  },

  actions: {
    async loadDashboards() {
      try {
        const response = await CompanyDashboardResource.getDashboards()
        this.dashboards = response
      } catch (error) {
        console.error('Error loading dashboards:', error)
        throw error
      }
    },

    async createDashboard(params: { name: string; description?: string; dashboard_type: string }) {
      try {
        const dashboard = await CompanyDashboardResource.createDashboard(params)
        this.dashboards = [dashboard, ...this.dashboards]
        return dashboard
      } catch (error) {
        console.error('Error creating dashboard:', error)
        throw error
      }
    },

    async updateDashboard(
      dashboardId: number,
      params: { name: string; description?: string; dashboard_type: string },
    ) {
      try {
        const dashboard = await CompanyDashboardResource.updateDashboard(dashboardId, params)
        const index = this.dashboards.findIndex((d) => d.id === dashboardId)
        if (index !== -1) {
          this.dashboards[index] = dashboard
        }
        if (this.activeDashboard?.id === dashboardId) {
          this.activeDashboard = {
            ...this.activeDashboard,
            ...dashboard,
          }
        }
        return dashboard
      } catch (error) {
        console.error('Error updating dashboard:', error)
        throw error
      }
    },

    async deleteDashboard(dashboardId: number) {
      try {
        await CompanyDashboardResource.deleteDashboard(dashboardId)
        this.dashboards = this.dashboards.filter((d) => d.id !== dashboardId)
        if (this.activeDashboard?.id === dashboardId) {
          this.clearDashboard()
        }
      } catch (error) {
        console.error('Error deleting dashboard:', error)
        throw error
      }
    },

    setActiveDashboard(dashboard: ICompanyDashboard) {
      this.activeDashboard = dashboard
    },

    async loadDashboard(dashboardId: number) {
      try {
        const dashboard = await CompanyDashboardResource.getDashboard(dashboardId)
        this.activeDashboard = dashboard
      } catch (error) {
        console.error('Error loading dashboard:', error)
        throw error
      }
    },

    setPeriod(period: [Date, Date], shouldFetch = true) {
      this.period = period
      if (shouldFetch) void this.fetchAllIndicatorsData()
      if (shouldFetch) void this.fetchAllTotalizersData()
    },

    resolveWidgetAggregationValues() {
      this.aggregationWidgets
        .filter((w) => isAggregationWidget(w.widget_type))
        .forEach((w) => {
        const dataField = w.config.data_field
        const sourceKey = dataField ? indicatorSourceKeyFromWidget(w, dataField) : null

        if (!sourceKey) {
          this.widgetData[w.id] = { value: null, unit: '', loading: false, error: true }
          return
        }

        const source = this.indicatorSources[sourceKey]
        if (!source) {
          this.widgetData[w.id] = { value: null, unit: '', loading: false, error: true }
          return
        }

        const aggregation =
          w.widget_type === 'gauge' || w.widget_type === 'status-panel' || isGroupWidget(w)
            ? 'last'
            : (w.config.aggregation || 'mean')
        const aggregations = source.aggregations || {}
        const value =
          aggregations[aggregation] ??
          aggregations.last ??
          aggregations.mean ??
          null

        this.widgetData[w.id] = {
          value,
          unit: resolveWidgetUnit(w, source.unit || ''),
          loading: false,
          error: value === null,
        }
      })

      this.resolvePieChartWidgetStates()
    },

    resolvePieChartWidgetStates() {
      this.aggregationWidgets
        .filter((w) => w.widget_type === 'pie-chart' || (w.widget_type === 'bar-chart' && isGroupWidget(w)))
        .forEach((w) => {
          const dataFields = resolveWidgetDataFields(w.config)
          const hasSourceId = isGroupWidget(w)
            ? Boolean(getWidgetGroupId(w))
            : Boolean(getWidgetSensorId(w))

          if (!hasSourceId || dataFields.length === 0) {
            this.widgetData[w.id] = { value: null, unit: '', loading: false, error: true }
            return
          }

          const hasAnySource = dataFields.some((dataField) => {
            const key = indicatorSourceKeyFromWidget(w, dataField)
            return key != null && this.indicatorSources[key] != null
          })

          if (!hasAnySource) {
            this.widgetData[w.id] = { value: null, unit: '', loading: false, error: true }
            return
          }

          this.widgetData[w.id] = { value: null, unit: '', loading: false, error: false }
        })
    },

    async fetchAllIndicatorsData() {
      if (!this.activeDashboard || !this.period) return

      const aggregationWidgets = this.aggregationWidgets

      const indicatorWidgets = this.aggregationWidgets.filter((w) => usesWidgetDataFetch(w))

      indicatorWidgets.forEach((w) => {
        this.widgetData[w.id] = { value: null, unit: '', loading: true, error: false }
      })

      try {
        const [start, end] = this.period
        const results = await CompanyDashboardResource.getIndicatorsData({
          dashboardId: this.activeDashboard.id,
          startDate: start.toISOString(),
          endDate: end.toISOString(),
        })

        this.indicatorSources = {}
        results.forEach((r) => {
          const key = indicatorSourceKeyFromResult(r)
          if (!key) return
          this.indicatorSources[key] = r
        })

        this.resolveWidgetAggregationValues()
      } catch (error) {
        console.error('Error fetching indicators data:', error)
        aggregationWidgets.forEach((w) => {
          if (usesWidgetDataFetch(w)) {
            this.widgetData[w.id] = { value: null, unit: '', loading: false, error: true }
          }
        })
      }
    },

    async fetchAllTotalizersData() {
      if (!this.activeDashboard || !this.period) return

      const totalizerWidgets = this.widgets.filter((w) => w.widget_type === 'totalizers')
      if (totalizerWidgets.length === 0) return

      totalizerWidgets.forEach((w) => {
        this.totalizerWidgetStates[w.id] = {
          demand: null,
          consumption: null,
          utilization: null,
          mixing_cycle: null,
          loading: true,
          error: false,
        }
      })

      try {
        const [start, end] = this.period
        const results = await CompanyDashboardResource.getTotalizersData({
          dashboardId: this.activeDashboard.id,
          startDate: start.toISOString(),
          endDate: end.toISOString(),
        })

        this.totalizerSources = {}
        results.forEach((source: ITotalizerSourceData) => {
          this.totalizerSources[String(source.sensor_id)] = source
        })

        this.resolveTotalizerWidgetValues()
      } catch (error) {
        console.error('Error fetching totalizers data:', error)
        totalizerWidgets.forEach((w) => {
          this.totalizerWidgetStates[w.id] = {
            demand: null,
            consumption: null,
            utilization: null,
            mixing_cycle: null,
            loading: false,
            error: true,
          }
        })
      }
    },

    resolveTotalizerWidgetValues() {
      this.widgets
        .filter((w) => w.widget_type === 'totalizers')
        .forEach((w) => {
          const sensorId = w.config.sensor_ids[0]
          const source = this.totalizerSources[sensorId]

          if (!source) {
            this.totalizerWidgetStates[w.id] = {
              demand: null,
              consumption: null,
              utilization: null,
              mixing_cycle: null,
              loading: false,
              error: true,
            }
            return
          }

          this.totalizerWidgetStates[w.id] = {
            demand: source.demand,
            consumption: source.consumption,
            utilization: source.utilization,
            mixing_cycle: source.mixing_cycle || null,
            loading: false,
            error: false,
          }
        })
    },

    async fetchBarChartData(windowPeriod: string, widgetId: string) {
      if (!this.activeDashboard || !this.period) return

      const barChartWidgets = this.widgets.filter((w) => w.widget_type === 'bar-chart')
      if (barChartWidgets.length === 0) return

      this.barChartWidgetStates[widgetId] = { loading: true, error: false }

      try {
        const [start, end] = this.period
        const results = await CompanyDashboardResource.getBarChartData({
          dashboardId: this.activeDashboard.id,
          startDate: start.toISOString(),
          endDate: end.toISOString(),
          windowPeriod,
        })

        results.forEach((r) => {
          if (r.sensor_id == null) return
          const key = barChartSourceKey(windowPeriod, String(r.sensor_id), r.data_field)
          this.barChartSources[key] = r
        })

        this.barChartWidgetStates[widgetId] = { loading: false, error: false }
      } catch (error) {
        console.error('Error fetching bar chart data:', error)
        this.barChartWidgetStates[widgetId] = { loading: false, error: true }
      }
    },

    async addWidget(dashboardId: number, params: object) {
      try {
        const widget = await CompanyDashboardResource.createWidget(dashboardId, params)
        if (this.activeDashboard) {
          this.activeDashboard.dashboard_widgets.push(widget)
        }
        return widget
      } catch (error) {
        console.error('Error adding widget:', error)
        throw error
      }
    },

    async updateWidget(dashboardId: number, widgetId: number, params: object) {
      try {
        const updated = await CompanyDashboardResource.updateWidget(dashboardId, widgetId, params)
        if (this.activeDashboard) {
          const idx = this.activeDashboard.dashboard_widgets.findIndex((w) => Number(w.id) === widgetId)
          if (idx !== -1) {
            this.activeDashboard.dashboard_widgets[idx] = updated
          }
        }

        if (updated && isAggregationWidget(updated.widget_type)) {
          this.resolveWidgetAggregationValues()
        }

        if (updated && updated.widget_type === 'pie-chart') {
          this.resolvePieChartWidgetStates()
        }

        return updated
      } catch (error) {
        console.error('Error updating widget:', error)
        throw error
      }
    },

    async removeWidget(dashboardId: number, widgetId: number) {
      try {
        await CompanyDashboardResource.deleteWidget(dashboardId, widgetId)
        if (this.activeDashboard) {
          this.activeDashboard.dashboard_widgets = this.activeDashboard.dashboard_widgets.filter(
            (w) => Number(w.id) !== widgetId
          )
        }
        delete this.widgetData[String(widgetId)]
        delete this.barChartWidgetStates[String(widgetId)]
      } catch (error) {
        console.error('Error removing widget:', error)
        throw error
      }
    },

    async updateLayout(dashboardId: number, widgets: Array<{ id: number; x: number; y: number; w: number; h: number }>) {
      try {
        await CompanyDashboardResource.updateLayout(dashboardId, widgets)
      } catch (error) {
        console.error('Error updating layout:', error)
      }
    },

    clearDashboard() {
      this.activeDashboard = null
      this.period = null
      this.widgetData = {}
      this.indicatorSources = {}
      this.barChartSources = {}
      this.barChartWidgetStates = {}
    },
  },
})
